/**
 * K&J entrée 262 (02/10/2026) : éco-contribution au panier (voir snippets/kj-eco.liquid).
 * Après chaque écriture du panier (événement cart:updated d'un autre composant ; panier rapide : une fois par rafale, 900 ms
 * après la dernière écriture, plus sur « kj:panier-calme » qui la relançait en boucle), recalcule k = somme (quantité × équivalents colis de 6) des lignes facturées (hors _offert, hors éco)
 * et pose la variante ECO-CONTRIB-1 (0,18 € HT) en quantité k ; retire la ligne si k = 0. Avant le paiement : dernière vérification.
 * 13/10 (entrée 337) : éco en UNITÉS, 1 u = 1 colis de 6 (comme la ligne ECO de HelloHarel). Avant : une variante par k
 * (ECO-CONTRIB-k, quantité 1, « x1 0,90 € ») ; une telle ligne encore au panier est remplacée à la synchro suivante.
 * La ligne porte la propriété _offert = « eco » : le franco 12 colis (SupaEasy) ne compte pas les lignes _offert ; la prime
 * ne regarde que les produits étiquetés prime-* ; les compteurs et le badge « Offert » l'excluent par son type de produit.
 * Le compteur de l'en-tête retire les unités d'éco (les composants du thème y posent item_count de Shopify, qui les compte).
 */
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

const el = document.getElementById('kj-eco-donnees');
if (el && !window.kjEco) {
  const D = JSON.parse(el.textContent);
  const ECO = new Set(D.k.map(String));
  const UN = String(D.u1 || D.k[0]); // variante ECO-CONTRIB-1 : 0,18 € HT, posée en quantité k
  const estEco = (l) => ECO.has(String(l.variant_id)) || l.product_type === 'Éco-contribution';
  let ecoQte = 0; // unités d'éco dans le dernier panier connu (pour corriger le compteur de l'en-tête)
  const kjFile = (job) => (window.kjPanierFile ? window.kjPanierFile.run(job) : job());
  let minuterie = null;
  // Entrée 381 : dernier panier connu (réponse d'une écriture K&J) : sert à calculer l'éco d'une écriture à venir
  // (window.kjEco.majPour) et évite une relecture /cart.js quand il est frais.
  let dernier = null;
  let frais = false;

  const lirePanier = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const besoin = (panier) => {
    let k = 0;
    for (const l of panier.items) {
      if (estEco(l)) continue;
      if (l.properties && l.properties._offert) continue;
      k += l.quantity * (D.uvc[String(l.variant_id)] || 1);
    }
    return k;
  };

  const majCompteur = (n) => {
    document.querySelectorAll('cart-icon [data-ref="cart-count"]').forEach((c) => { c.textContent = n; });
  };

  const sections = () => {
    const out = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.dataset.sectionId) out.push(drawer.dataset.sectionId);
    document.querySelectorAll('cart-component, [data-kj-section-panier]').forEach((c) => {
      const s = c.closest('.shopify-section');
      if (s) out.push(s.id.replace('shopify-section-', ''));
    });
    return [...new Set(out)];
  };

  const compteur = (panier) => {
    majCompteur(panier.items.reduce((s, l) => s + (estEco(l) ? 0 : l.quantity), 0));
  };

  // Entrée 382 : état de l'éco sur un panier (k voulu, lignes d'éco, bonne ligne, juste ou non).
  const etat = (panier) => {
    const k = besoin(panier);
    const lignes = panier.items.filter(estEco);
    const bonne = k > 0 ? lignes.find((l) => String(l.variant_id) === UN && l.properties && l.properties._offert === 'eco') : null;
    const ok = (k === 0 && lignes.length === 0) || (lignes.length === 1 && !!bonne && bonne.quantity === k);
    return { k, lignes, bonne, ok };
  };

  // K&J (09/10, panier rapide) : synchro en arrière-plan, une fois par rafale de clics, au plus juste en requêtes.
  // Avant : relancée à chaque « kj:panier-calme »… qu'elle provoquait elle-même : une lecture /cart.js par seconde
  // en continu, et chaque clic du client attendait derrière une synchro (≈ 1,2 s) dans la file.
  async function synchroniser() {
    const panier = frais && dernier ? dernier : await lirePanier();
    frais = false;
    dernier = panier;
    const { k, lignes, bonne, ok } = etat(panier);
    ecoQte = lignes.reduce((s, l) => s + l.quantity, 0);
    compteur(panier); // l'éco-contribution ne compte pas : le nombre d'articles est déjà juste
    if (ok) return panier;
    const secs = sections();
    const base = { sections: secs.join(','), sections_url: location.pathname };
    // La bonne ligne passe à k unités ; toute autre ligne d'éco (ancienne variante, doublon) est retirée.
    const maj = {};
    lignes.forEach((l) => { maj[l.key] = l === bonne ? k : 0; });
    const aMettreAJour = Object.keys(maj).length > 0;
    let data;
    // Ajout de la ligne si elle manque, puis mise à jour des autres : la dernière requête porte les sections.
    if (k > 0 && !bonne) {
      const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(UN), quantity: k, properties: { _offert: 'eco' } }], ...(aMettreAJour ? {} : base) }) });
      data = await r.json();
      if (!r.ok) throw new Error(data.description || 'éco-contribution : ajout refusé');
    }
    if (aMettreAJour) {
      const r = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: maj, ...base }) });
      data = await r.json();
      if (!r.ok) throw new Error(data.description || 'éco-contribution : mise à jour refusée');
    }
    ecoQte = k;
    // Entrée 382 : ligne d'éco seulement ajoutée (add.js ne renvoie que la ligne ajoutée) : dernier panier = panier lu + cette
    // ligne. Avant : inconnu, le clic suivant de la box partait sans l'éco et une 2e écriture suivait 600 ms après.
    dernier = data && data.token && Array.isArray(data.items) ? data
      : data && Array.isArray(data.items) ? { ...panier, items: [...panier.items.filter((l) => !data.items.some((n) => n.key === l.key)), ...data.items] }
        : null;
    // Page panier : on remplace son composant par sa version fraîche (avant : rechargement complet de la page).
    const cartComp = document.querySelector('cart-component');
    if (cartComp) {
      const s = cartComp.closest('.shopify-section');
      const html = s && data && data.sections && data.sections[s.id.replace('shopify-section-', '')];
      const neuf = html && new DOMParser().parseFromString(html, 'text/html').querySelector('cart-component');
      if (neuf) cartComp.replaceWith(neuf);
      else { location.reload(); return panier; }
    }
    const ev = new CartUpdatedEvent({}, { source: 'kj-eco', sections: data && data.sections ? data.sections : undefined,
      itemCount: panier.items.reduce((s, l) => s + (estEco(l) ? 0 : l.quantity), 0) });
    // Les lignes payantes n'ont pas bougé : les cartes de la grille se recalent sur ce panier sans requête.
    ev.kjPanier = Promise.resolve(panier);
    ev.kjFrais = true;
    document.dispatchEvent(ev);
    return panier;
  }

  // Une seule synchro par rafale : 900 ms après la dernière écriture, et jamais devant un clic du client
  // (si la file a du travail, on repousse).
  const DELAI = 600; // entrée 381 : 900 → 600 ms (l'éco des clics de la box part déjà avec le clic)
  const planifier = (delai = DELAI) => {
    clearTimeout(minuterie);
    minuterie = setTimeout(() => {
      if (window.kjPanierFile && window.kjPanierFile.attente) { planifier(); return; }
      // Entrée 382 : panier frais (réponse de la dernière écriture) et éco déjà juste (cas courant : la box l'envoie avec
      // le clic) : rien à écrire, on ne passe pas par la file. Avant : un tour de file vide, qui faisait croire périmé
      // le rendu de la box en cours de lecture (la box restait sur son affichage au clic).
      if (frais && dernier) {
        const e = etat(dernier);
        if (e.ok) {
          frais = false;
          ecoQte = e.lignes.reduce((s, l) => s + l.quantity, 0);
          compteur(dernier);
          return;
        }
      }
      kjFile(synchroniser).catch((e) => console.warn('kj-eco', e));
    }, delai);
  };

  // Entrée 381 : correction d'éco à joindre à une mise à jour { clé de ligne: quantité } sur le dernier panier connu.
  // Renvoie {} si rien à faire ou si l'on ne sait pas (pas de ligne d'éco à mettre à jour : la synchro l'ajoutera).
  const majPour = (updates) => {
    if (!dernier || !dernier.items) return {};
    const items = dernier.items.map((l) => (updates[l.key] != null ? { ...l, quantity: updates[l.key] } : l));
    const k = besoin({ items });
    const lignes = items.filter(estEco);
    const bonne = lignes.find((l) => String(l.variant_id) === UN && l.properties && l.properties._offert === 'eco');
    if (k > 0 && !bonne) return {};
    const maj = {};
    lignes.forEach((l) => { const voulu = l === bonne ? k : 0; if (voulu !== l.quantity) maj[l.key] = voulu; });
    return maj;
  };

  window.kjEco = { synchroniser: () => kjFile(synchroniser), majPour, estEco };
  document.addEventListener(ThemeEvents.cartUpdated, (e) => {
    const d = (e.detail && e.detail.data) || {};
    if (d.source !== 'kj-eco') {
      frais = false;
      // Panier COMPLET seulement (token + items) : la réponse d'un add.js ne contient que les lignes ajoutées.
      if (e.kjPanier) e.kjPanier.then((p) => { if (p && p.token && Array.isArray(p.items)) { dernier = p; frais = true; } }).catch(() => {});
    }
    if (d.source !== 'kj-eco') planifier();
    // Compteur de l'en-tête : item_count de Shopify compte les unités d'éco (k depuis le 13/10, 1 avant) ; kj-eco et
    // kj-palier envoient déjà un nombre sans l'éco. On corrige après le composant du compteur (même tour d'événement).
    if (d.source === 'kj-eco' || d.source === 'kj-palier' || typeof d.itemCount !== 'number' || !ecoQte) return;
    const n = Math.max(0, d.itemCount - ecoQte);
    setTimeout(() => majCompteur(n), 0);
  });
  planifier(150); // à l'arrivée sur la page : tout de suite

  // Paiement : on vérifie la ligne avant de partir (formulaire du panier ou lien vers /checkout).
  // Panier rapide (12/10) : la vérification attend la fin de la file (écritures du client) puis corrige l'éco ; cela peut
  // prendre quelques secondes juste après un changement. Le bouton montre qu'il travaille (roue, aria-busy) et un 2e clic
  // ne relance rien. Si le tiroir ou la page panier s'est redessiné pendant l'attente, le bouton cliqué n'est plus dans
  // la page : on repart du bouton vivant du même formulaire (avant : le clic se perdait, il fallait recliquer).
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[name="checkout"], a[href*="/checkout"], button[form="cart-form"][name="checkout"]');
    if (!b || b.dataset.kjEcoOk) return;
    e.preventDefault();
    e.stopPropagation();
    if (b.dataset.kjEcoAttente) return;
    const formId = b.getAttribute('form');
    b.dataset.kjEcoAttente = '1';
    b.classList.add('is-loading');
    b.setAttribute('aria-busy', 'true');
    try { await window.kjEco.synchroniser(); } catch (err) { console.warn('kj-eco', err); }
    delete b.dataset.kjEcoAttente;
    b.classList.remove('is-loading');
    b.removeAttribute('aria-busy');
    const vivant = b.isConnected ? b : (formId && document.querySelector(`[name="checkout"][form="${formId}"]`)) || b;
    vivant.dataset.kjEcoOk = '1';
    setTimeout(() => { delete vivant.dataset.kjEcoOk; }, 3000); // retour arrière du navigateur : la vérification revient
    if (vivant.form) { vivant.form.requestSubmit ? vivant.form.requestSubmit(vivant) : vivant.form.submit(); }
    else if (vivant.href) location.href = vivant.href;
    else vivant.click();
  }, true);
}
